import { useEffect, useState } from "react";
import api from "../../shared/utils/request";
import PageLoader from "../shared/pageLoader";
import LoginWarning from "../shared/warningPages";
import AccountSidebar from "../shared/accountSidebar";
import AddressModal from "../checkout/AddressModal";
import ConfirmationModal from "../checkout/ConfirmationModal";
import { usePageSeo } from "../../shared/seo";
import { toast } from "react-hot-toast";

const AddressBook = () => {
  usePageSeo("addressBook");
  const isToken = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [defaultLoading, setDefaultLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAddressId, setDeleteAddressId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (isToken) {
      fetchAddresses();
    } else {
      setLoading(false);
    }
  }, [isToken]);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const data = await api.get("customer/addresses");
      if (data) {
        setAddresses(data);
      }
    } catch (error) {
      console.error("Failed to fetch addresses:", error);
      toast.error("Failed to load addresses");
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      setDefaultLoading(addressId);
      const data = await api.put(`customer/addresses/${addressId}`, {
        is_default: 1,
      });
      if (data) {
        toast.success("Default address updated");
        fetchAddresses();
      }
    } catch (error) {
      console.error("Failed to set default address:", error);
      toast.error("Failed to set default address");
    } finally {
      setDefaultLoading(null);
    }
  };

  const confirmDelete = (addressId) => {
    setDeleteAddressId(addressId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteAddress = async () => {
    try {
      setDeleteLoading(true);
      const data = await api.delete(`customer/addresses/${deleteAddressId}`);
      if (data) {
        toast.success(data?.message || "Address deleted successfully");
        fetchAddresses();
        setShowDeleteConfirm(false);
        setDeleteAddressId(null);
      }
    } catch (error) {
      console.error("Failed to delete address:", error);
      toast.error("Failed to delete address");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isToken && !loading) return <LoginWarning />;

  return (
    <>
      {(loading || defaultLoading) && <PageLoader />}

      <section
        className="py-40"
        style={{ background: "linear-gradient(135deg, #f6faf7 0%, #edf5f0 100%)" }}
      >
        <div className="container container-lg">
          <div className="row g-3">
            <div className="col-lg-3 mb-16 mb-lg-0">
              <AccountSidebar />
            </div>

            <div className="col-lg-9">
              <div className="mb-16 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h3 className="mb-2 fw-bold text-main-700">Address Book</h3>
                  <p className="text-neutral-600 text-sm mb-0 d-none d-sm-block">
                    Manage your saved delivery addresses
                  </p>
                </div>
                <button
                  onClick={() => setShowAddAddress(true)}
                  className="btn btn-main d-inline-flex align-items-center gap-6 rounded-pill px-20 py-10 fw-semibold mt-2"
                  style={{ transform: "none" }}
                >
                  <i className="ph ph-plus" />
                  Add New Address
                </button>
              </div>

              {!loading && addresses.length === 0 ? (
                <div className="rounded-12 bg-white border border-main-100 text-center px-16 py-40" style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}>
                  <i className="ph ph-map-pin text-6xl text-neutral-300 mb-16 d-block" />
                  <h5 className="fw-bold mb-8">No Addresses Yet</h5>
                  <p className="text-neutral-500 mb-24" style={{ fontSize: "0.85rem" }}>
                    Add a delivery address to speed up your checkout
                  </p>
                  <button
                    onClick={() => setShowAddAddress(true)}
                    className="btn btn-main d-inline-flex align-items-center gap-6 rounded-pill mx-auto"
                    style={{ width: "fit-content", transform: "none" }}
                  >
                    <i className="ph ph-plus" />
                    Add New Address
                  </button>
                </div>
              ) : (
                <div className="row g-3">
                  {addresses.map((addr) => (
                    <div key={addr.id} className="col-md-6">
                      <div
                        className={`rounded-12 bg-white h-100 p-12 p-sm-16 position-relative ${
                          addr?.is_default === 1
                            ? "border border-main-600"
                            : "border border-main-100"
                        }`}
                        style={{
                          boxShadow:
                            addr?.is_default === 1
                              ? "0 4px 16px rgba(26, 107, 48, 0.15)"
                              : "0 2px 8px rgba(26, 107, 48, 0.05)",
                        }}
                      >
                        <div className="d-flex align-items-start gap-10 mb-10">
                          <div
                            className={`d-flex align-items-center justify-content-center rounded-10 flex-shrink-0 ${
                              addr?.is_default === 1 ? "bg-main-600 text-white" : "bg-main-50 text-main-600"
                            }`}
                            style={{ width: "38px", height: "38px" }}
                          >
                            <i className="ph ph-house" style={{ fontSize: "18px" }} />
                          </div>
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex align-items-center gap-6 flex-wrap">
                              <h6 className="mb-0 fw-bold text-truncate text-main-700" style={{ fontSize: "14px" }}>
                                {addr.name}
                              </h6>
                            </div>
                            <p className="mb-0 text-neutral-500 d-flex align-items-center gap-4 text-xs">
                              <i className="ph ph-phone text-main-600" />
                              {addr.phone}
                            </p>
                          </div>
                        </div>

                        <div
                          className="rounded-10 p-10"
                          style={{ background: "#f6faf7", border: "1px solid #eef5f0" }}
                        >
                          <p className="mb-0 text-neutral-700" style={{ fontSize: "12.5px", lineHeight: "1.5" }}>
                            {[addr.address_line_1, addr.address_line_2].filter(Boolean).join(", ")}
                          </p>
                          <p className="mb-0 text-neutral-500" style={{ fontSize: "12.5px" }}>
                            {[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
                          </p>
                          {/* {addr.distance && (
                            <p className="mb-0 mt-4 d-flex align-items-center gap-4 text-xs" style={{ color: "#166534" }}>
                              <i className="ph ph-path" />
                              ~{addr.distance} km away
                            </p>
                          )} */}
                        </div>

                        <div className="d-flex align-items-center justify-content-between gap-6 pt-10 mt-10" style={{ borderTop: "1px solid #eef5f0" }}>
                          {addr?.is_default !== 1 ? (
                            <button
                              onClick={() => handleSetDefault(addr.id)}
                              disabled={defaultLoading === addr.id}
                              className="btn btn-sm btn-outline-main d-inline-flex align-items-center gap-4 rounded-pill"
                              style={{ fontSize: "11px", transform: "none" }}
                            >
                              {defaultLoading === addr.id ? (
                                <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                              ) : (
                                <>
                                  <i className="ph ph-check-circle" />
                                  Set as Default
                                </>
                              )}
                            </button>
                          ) : (
                            <span />
                          )}
                          {addr?.is_default === 1 ? (
                            <span className="badge bg-main-600 text-white px-10 py-6 rounded-pill" style={{ fontSize: "10px" }}>
                              <i className="ph ph-check me-4" />
                              Default
                            </span>
                          ) : (
                            <button
                              onClick={() => confirmDelete(addr.id)}
                              className="btn btn-sm d-inline-flex align-items-center gap-4 rounded-pill text-danger-600"
                              style={{
                                fontSize: "11px",
                                background: "#ffebee",
                                border: "1px solid #f5c6cb",
                                transform: "none",
                              }}
                            >
                              <i className="ph ph-trash" />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <AddressModal
        show={showAddAddress}
        onClose={() => setShowAddAddress(false)}
        editingAddress={null}
        onSaveAddress={() => {
          toast.success("Address added successfully");
          fetchAddresses();
        }}
      />

      <ConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteAddressId(null);
        }}
        onConfirm={handleDeleteAddress}
        loading={deleteLoading}
        title="Delete Address?"
        message="Are you sure you want to delete this address?"
      />
    </>
  );
};

export default AddressBook;
